import React, { useEffect, useState, useCallback } from "react";
import { useSelector } from "react-redux";
import {
  messageIcons,
  mailIcon,
  phoneIcon,
  depositIcon,
  commentIcon,
  clockIcon,
  chainIcon,
  newUserIcon,
  contractIcon,
  referredIcon,
  customerCategoryIcon,
  referredCategoryIcon,
} from "../../utils/historyIcons/historyIcons";
import { convertSeconds } from "../../utils/parse/parse";
import { getAllDepositsByCompanyService } from "../../services/deposit/services";
import { useTranslator } from "../../context/translator";
import { useGetCompanyQuery, useGetUserByIdQuery } from "store/api/apiSlice";

const TYPES = {
  EMAIL: "Email",
  SMS: "Message",
  CL: "Call",
  DP: "Deposit",
  CMM: "Comment",
  LG: "Login",
  RESET: "Reset Password",
  NEWUSER: "New user",
  CNT: "Contract",
  CNTF: "Contract File",
  CUCAT: "Customer Category",
  RECAT: "Referred Category",
  RE: "Referred",
  SCI: "Document",
};

const ICONS = {
  EMAIL: mailIcon,
  SMS: messageIcons,
  CL: phoneIcon,
  DP: depositIcon(),
  CMM: commentIcon,
  LG: clockIcon,
  RESET: chainIcon(),
  NEWUSER: newUserIcon,
  CNT: contractIcon(),
  CNTF: customerCategoryIcon(),
  CUCAT: referredCategoryIcon(),
  RE: referredIcon(),
  SCI: contractIcon(),
};

const HistoryCard = ({ histories }) => {
  const { user } = useSelector((state) => state.user);
  const { data: userProfile, isLoading: userLoading } = useGetUserByIdQuery(
    user.id,
    {
      skip: !user?.id,
      pollingInterval: 60000,
      refetchOnFocus: true,
      refetchOnReconnect: true,
    },
  );

  const { data: company, isLoading: companyLoading } = useGetCompanyQuery(
    userProfile?.user?.companyId,
    {
      skip: !userProfile?.user?.companyId,
    },
  );
  const { translate } = useTranslator();
  const [depositTypes, setDepositTypes] = useState([]);

  console.log("Histories in HistoryCard:", histories); // Debugging line

  const hasDeposit = histories.some((h) => h.type === "DP");

  const getDepositTypesCall = useCallback(async () => {
    try {
      const data = await getAllDepositsByCompanyService({
        companyId: company?._id,
      });
      setDepositTypes(data?.deposits || []);
    } catch (err) {
      console.error("Error loading deposit types", err);
      setDepositTypes([]);
    }
  }, [company]);

  useEffect(() => {
    if (hasDeposit) getDepositTypesCall();
  }, [hasDeposit, getDepositTypesCall]);

  const getDepositTypeName = (depositCode) =>
    depositTypes.find((t) => t.code === depositCode)?.name || "";

  const getText = (history) => {
    const { message, html, duration, customer, comment } = history;
    const client = customer?.[0];

    const texts = {
      SMS: message,
      EMAIL: html,
      CL: `Voice Call ${convertSeconds(duration)} Customer: ${
        client?.firstName || ""
      } ${client?.lastName || ""}`,
      DP: comment,
      CMM: comment,
      LG: comment,
      CNT: comment,
      CNTF: comment,
      RESET: "Reset Password",
      NEWUSER: html,
      CUCAT: comment,
      RE: comment,
      SCI: comment,
    };

    return texts[history?.type] || "";
  };

  if (userLoading || companyLoading) return null;

  return (
    <>
      {histories.map((his, index) => {
        const {
          type,
          user: historyUser,
          dateTime,
          amount,
          depositType,
          templateName,
        } = his;
        const customer = his.customer?.[0];
        const date = new Date(dateTime);
        const formattedDate = `${date.toLocaleDateString()} - ${date.toLocaleTimeString()}`;
        const icon = ICONS[type];
        const label = TYPES[type];

        return (
          <div key={`${his._id}-${index}`} className="flex items-start gap-2.5">
            {icon}

            <div className="flex flex-col w-full leading-1.5 p-4 border-gray-200 bg-gray-100 rounded-e-xl rounded-es-xl dark:bg-gray-700">
              {/* Header */}
              <div className="flex items-center space-x-2 rtl:space-x-reverse">
                <span className="text-sm font-semibold text-gray-900 dark:text-white">
                  {historyUser?.firstName} {historyUser?.lastName}
                </span>
                <span className="text-sm font-normal text-gray-500 dark:text-gray-400">
                  {label}
                </span>
              </div>

              {/* Body */}
              {type === "DP" ? (
                <>
                  <div className="flex flex-wrap gap-6 py-2.5">
                    <InfoLabel
                      label={translate("customer")}
                      value={`${customer?.firstName || ""} ${
                        customer?.lastName || ""
                      }`}
                    />
                    <InfoLabel
                      label={translate("deposit-type")}
                      value={getDepositTypeName(depositType)}
                    />
                    <InfoLabel label={translate("amount")} value={amount} />
                  </div>
                  <TextContent text={getText(his)} />
                </>
              ) : type === "EMAIL" || type === "NEWUSER" ? (
                <div className="flex flex-wrap gap-6 py-2.5">
                  <InfoLabel
                    label={translate("customer")}
                    value={`${customer?.firstName || ""} ${
                      customer?.lastName || ""
                    }`}
                  />
                  <InfoLabel label="Email" value={customer?.email} />
                  <InfoLabel
                    label={translate("template-name")}
                    value={templateName}
                  />
                </div>
              ) : (
                <TextContent text={getText(his)} />
              )}

              {/* Footer */}
              <span className="text-sm font-normal text-gray-500 dark:text-gray-400">
                {formattedDate}
              </span>
            </div>
          </div>
        );
      })}
    </>
  );
};

const InfoLabel = ({ label, value }) => (
  <div>
    <span className="text-sm font-bold text-gray-900 dark:text-white">
      {label}:{" "}
      <span className="text-sm font-normal text-gray-900 dark:text-white">
        {value || "-"}
      </span>
    </span>
  </div>
);

const TextContent = ({ text }) =>
  text ? (
    <p className="text-sm font-normal py-2.5 text-gray-900 dark:text-white">
      {text}
    </p>
  ) : null;

export default React.memo(HistoryCard);
